// @noflow
import type { Language } from '@/packs/localisation'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Image from '@/components/shared/elements/Image/Image'
import Video from '@/components/shared/elements/Video/Video'

import STYLES from './RealStoriesVideoCarousel.module.sass'

import Text from '../Text/Text'

type Props = {
  preferredLanguage: Language
}

const VIDEOS = [
  {
    id: 1,
    slug: 'pt_ugc_video_1'
  },
  {
    id: 2,
    slug: 'pt_ugc_video_2'
  },
  {
    id: 3,
    slug: 'pt_ugc_video_3'
  }
]

const RealStoriesVideoCarousel = ({
  preferredLanguage
}: Props): React.ReactElement => {
  const [playing, setPlaying] = useState(0)
  const [muted, setMuted] = useState(false)
  const [manualOverride, setManualOverride] = useState({
    active: false,
    videoId: 0
  })

  const containerRef = useRef<HTMLDivElement>(null)

  const videoRefs = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null)
  ]

  const namespace = 'paid_traffic'
  const copyContext = 'phase_3.real_stories'

  const { t } = useTranslation(namespace)

  const numberOfVideos = preferredLanguage === 'fr' ? 2 : VIDEOS.length

  const togglePlaying = useCallback(
    (videoId: number) => {
      if (videoId !== playing) {
        setPlaying(videoId)
        setManualOverride({ active: true, videoId })
      } else {
        // Pause if the video is already playing
        setPlaying(0)
        setManualOverride({ active: true, videoId: 0 })
      }
    },
    [playing]
  )

  const toggleMute = useCallback(() => {
    setMuted(!muted)
  }, [muted])

  const manualOverrideRef = useRef(manualOverride)

  useEffect(() => {
    manualOverrideRef.current = manualOverride
  }, [manualOverride])

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return

      setManualOverride({ active: false, videoId: 0 })

      const container = containerRef.current
      const containerCenter = container.offsetWidth / 2 + container.scrollLeft

      const distances = videoRefs.map((ref) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect()
          const videoCenter =
            rect.left +
            rect.width / 2 +
            container.scrollLeft -
            container.getBoundingClientRect().left
          return Math.abs(containerCenter - videoCenter)
        } else {
          return Infinity // If the ref is not set, return a large distance
        }
      })

      const closestIndex = distances.indexOf(Math.min(...distances))
      if (!manualOverrideRef.current.active) {
        // If manual override is not active, change the playing video
        setPlaying(closestIndex + 1)
        setManualOverride({ active: false, videoId: closestIndex + 1 })
      }
    }

    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll, { passive: true })

    handleScroll() // initial check

    return () => container.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty to prevent infinite re-rendering

  return (
    <div ref={containerRef} className={STYLES.container}>
      {VIDEOS.slice(0, numberOfVideos).map((video) => {
        return (
          <div key={video.id} className={STYLES.videoContainer}>
            <div>
              <div
                ref={videoRefs[video.id - 1]}
                className={STYLES.videoWrapper}
              >
                <Video
                  loop
                  playsInLine
                  poster={`https://butternutbox.com/asset_management/assets/${video.slug}_poster/${preferredLanguage}`}
                  playing={playing === video.id}
                  controls={false}
                  alt=""
                  muted={muted}
                  slug={video.slug}
                  playerHeight={399}
                  playerWidth={319}
                  video={{
                    format: 'webm'
                  }}
                />
                <button
                  type="button"
                  className={STYLES.playButton}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={() => {
                    togglePlaying(video.id)
                  }}
                >
                  <Image
                    alt={
                      playing === video.id
                        ? t('phase_3.real_stories.pause_alt')
                        : t('phase_3.real_stories.play_alt')
                    }
                    slug={
                      playing === video.id
                        ? 'pause-button-icon'
                        : 'play-button-icon'
                    }
                    image={{
                      height: 32,
                      width: 32,
                      resizeMode: 'resize_to_fit',
                      quality: 'auto:best',
                      retina: true
                    }}
                  />
                </button>
                <button
                  type="button"
                  className={STYLES.muteButton}
                  onClick={toggleMute}
                >
                  <Image
                    alt={
                      muted
                        ? t('phase_3.real_stories.unmute_alt')
                        : t('phase_3.real_stories.mute_alt')
                    }
                    slug={muted ? 'unmute-button-icon' : 'mute-button-icon'}
                    image={{
                      height: 24,
                      width: 24,
                      resizeMode: 'resize_to_fit',
                      quality: 'auto:best',
                      retina: true
                    }}
                  />
                </button>
              </div>
              <div className={STYLES.fiveStarsContainer}>
                {[1, 2, 3, 4, 5].map((star) => {
                  return (
                    <Image
                      key={`star-${star}`}
                      alt=""
                      slug="real-stories-star"
                      image={{
                        height: 24,
                        width: 24,
                        resizeMode: 'resize_to_fit',
                        quality: 'auto:best',
                        retina: true
                      }}
                    />
                  )
                })}
              </div>
              <Text
                variant="textRegular20"
                bold
                namespace={namespace}
                text={`${copyContext}.videos.video_${video.id}.quote`}
                element="h3"
                margin={false}
                align="left"
              />
            </div>
            <Text
              variant="textRegular16"
              colour="brandBlue400"
              namespace={namespace}
              text={`${copyContext}.videos.video_${video.id}.source`}
              element="p"
            />
          </div>
        )
      })}
    </div>
  )
}

export default RealStoriesVideoCarousel
